<template>
  <div class="pc__image-swiper" ref="imageCarouselRef" v-if="enableImageCarousel">
    <div class="pc__slide-wrapper">
      <div class="pc__slide" v-for="(image, index) in featureImageVariants" :key="index">
        <transition name="fade" mode="out-in">
          <ImageField
            class="pc__feature-img"
            :obj="{ url: image.src, alt: image.alt, width: 600, height: 750 }"
            :isLoadingLazy="isLoadingLazy"
            :key="image.src"
          />
        </transition>
      </div>
    </div>
    <div class="swiper-pagination"></div>
  </div>

  <template v-else>
    <transition name="fade" mode="out-in">
      <ImageField
        class="pc__feature-img"
        :obj="{ url: featureImage.src, alt: product.title, width: 600, height: 750 }"
        :isLoadingLazy="isLoadingLazy"
        :key="featureImage.src"
      />
    </transition>
    <ImageField
      :key="hoverImage ? hoverImage.src : featureImage.src"
      class="pc__hover-img"
      :obj="{ url: hoverImage ? hoverImage.src : featureImage.src, alt: product.title, width: 600, height: 750 }"
      :isLoadingLazy="isLoadingLazy"
    />
  </template>
</template>

<script setup>
  import { ref, computed, onMounted, watch, toRefs } from 'vue';
  import Swiper from 'swiper';
  import SwiperCore, { Navigation, EffectFade, Thumbs, Pagination, A11y } from 'swiper/core';
  import ImageField from '../image.vue';

  SwiperCore.use([Navigation, EffectFade, Thumbs, Pagination, A11y]);

  // Shoplift: on-model vs flatlay product card imagery
  const ON_MODEL_VS_FLATLAY_ORIGINAL_ID = '019fb2a0-1c21-71ff-a92a-e87a8b56febf';
  const ON_MODEL_VS_FLATLAY_VARIANT_ID = '019fb2a0-20cc-7b01-a46a-94cccd6d9893';

  const props = defineProps({
    enableImageCarousel: {
      type: Boolean,
      default: false,
    },
    selectedColor: {
      type: String,
      default: '',
    },
    useLifestyleImage: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
    },
    isLoadingLazy: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['abImageVariantChange']);

  const { enableImageCarousel, product, isLoadingLazy, selectedColor, useLifestyleImage } = toRefs(props);

  const imageCarouselRef = ref(null);
  const imageSwiper = ref(null);
  const cachedTestState = window.__stioOnModelVsFlatlayTestState;
  const isOnModelImageTestActive = ref(cachedTestState?.showOnModel === true);
  const isInOnModelVsFlatlayTest = ref(cachedTestState?.inTest === true);

  onMounted(() => {
    setOnModelImageTestState();

    if (enableImageCarousel.value) {
      initializeSwiper();
    }
  });

  watch(enableImageCarousel, (newVal) => {
    if (newVal) {
      initializeSwiper();
    } else if (imageSwiper.value) {
      imageSwiper.value.destroy();
    }
  });

  const featureImageVariants = computed(() => {
    const { images, title } = product.value;

    if (shouldUseOnModelImage.value) {
      return [onModelImage.value];
    }

    return window.getProductImages(images, selectedColor.value, title).slice(0, 4);
  });

  const featureImage = computed(() => {
    const { images, title } = product.value;

    if (shouldUseOnModelImage.value) {
      return onModelImage.value;
    }

    const imageData = window.getProductImages(images, selectedColor.value, title);
    const defaultImage = imageData.find((image) => !image.alt || !image.alt.toLowerCase().includes('alternative')) || imageData[0];
    const lifestyleImage = imageData.find((image) => image.alt && image.alt.toLowerCase().includes('alternative'));

    if (useLifestyleImage.value && lifestyleImage) {
      return lifestyleImage;
    }

    return defaultImage;
  });

  const hoverImage = computed(() => {
    const { images, title } = product.value;

    const imageData = window.getProductImages(images, selectedColor.value, title);
    const defaultImage = imageData.find((image) => !image.alt || !image.alt.toLowerCase().includes('alternative')) || imageData[0];

    if (shouldUseOnModelImage.value) {
      return defaultImage;
    }

    const filteredImageData = imageData.filter((image) => image.alt && image.alt.toLowerCase().includes('alternative'));
    const defaultHoverImage = filteredImageData[0];

    if (useLifestyleImage.value) {
      return defaultImage;
    }

    return defaultHoverImage;
  });

  const selectedColorVariant = computed(() => {
    const variants = product.value.variants || [];
    const normalizedSelectedColor = selectedColor.value?.toLowerCase();

    if (!normalizedSelectedColor) {
      return variants.find((variant) => variant.on_model_image?.src);
    }

    const selectedColorVariants = variants.filter((variant) => {
      return [variant.color, variant.option1].some((value) => value?.toLowerCase() === normalizedSelectedColor);
    });

    return (
      selectedColorVariants.find((variant) => variant.available && variant.on_model_image?.src) ||
      selectedColorVariants.find((variant) => variant.on_model_image?.src)
    );
  });

  const onModelImage = computed(() => selectedColorVariant.value?.on_model_image);

  const shouldUseOnModelImage = computed(() => {
    return isOnModelImageTestActive.value && onModelImage.value?.src;
  });

  // What the shopper actually sees — for Elevar/GTM attribution on the product card
  const abImageVariant = computed(() => {
    if (!isInOnModelVsFlatlayTest.value) return null;
    return shouldUseOnModelImage.value ? 'on_model' : 'flatlay';
  });

  watch(
    abImageVariant,
    (variant) => {
      emit('abImageVariantChange', variant);
    },
    { immediate: true }
  );

  const setOnModelImageTestState = async () => {
    const testState = await getOnModelImageTestState();
    isOnModelImageTestActive.value = testState.showOnModel;
    isInOnModelVsFlatlayTest.value = testState.inTest;
  };

  const AB_TEST_COLLECTION_HANDLES = new Set([
    'mens',
    'womens',
    'best-sellers',
    'mens-bestsellers',
    'womens-bestsellers',
    'hike',
    'mens-hike',
    'womens-hike',
    'bike',
    'mens-bike',
    'womens-bike',
    'swim',
    'mens-swim',
    'womens-swim',
    'run',
    'mens-run',
    'womens-run',
    'climb',
    'mens-climb',
    'womens-climb',
    'paddle',
    'mens-paddle',
    'womens-paddle',
    'camp',
    'mens-camp',
    'womens-camp',
    'fish',
    'mens-fish',
    'womens-fish',
    'ski-snowboard',
    'mens-ski-snowboard',
    'womens-ski-snowboard',
    'everyday',
    'mens-everyday',
    'womens-everyday',
    'new-arrivals',
    'mens-new-arrivals',
    'womens-new-arrivals',
    'mens-shirts',
    'womens-shirts',
    'mens-pants',
    'womens-pants',
    'mens-fleece',
    'womens-fleece',
    'mens-jackets',
    'womens-jackets',
    'mens-vests',
    'womens-vests',
    'mens-jackets-vests',
    'womens-jackets-vests',
    'mens-insulation',
    'womens-insulation',
    'mens-upf-sun-protection',
    'womens-upf-sun-protection',
    'mens-shorts',
    'womens-shorts',
    'mens-tees-hoodies',
    'womens-tees-hoodies',
    'womens-dresses-skirts',
    'spring-summer-collection',
    'fall-winter-collection',
    'sale',
    'mens-sale',
    'womens-sale',
    'outlet',
    'mens-outlet',
    'womens-outlet',
  ]);

  const isEligibleCollectionPage = () => {
    const match = window.location.pathname.match(/^\/collections\/([^/?#]+)/);
    return match ? AB_TEST_COLLECTION_HANDLES.has(match[1]) : false;
  };

  const getOnModelImageTestState = async () => {
    const inactiveState = { showOnModel: false, inTest: false };

    if (!isEligibleCollectionPage() || !window.shoplift) {
      return inactiveState;
    }

    if (window.__stioOnModelVsFlatlayTestState) {
      return window.__stioOnModelVsFlatlayTestState;
    }

    if (!window.__stioOnModelVsFlatlayTestPromise) {
      window.__stioOnModelVsFlatlayTestPromise = Promise.all([
        window.shoplift.isHypothesisActive(ON_MODEL_VS_FLATLAY_VARIANT_ID).then(Boolean).catch(() => false),
        window.shoplift.isHypothesisActive(ON_MODEL_VS_FLATLAY_ORIGINAL_ID).then(Boolean).catch(() => false),
      ])
        .then(([variantActive, originalActive]) => {
          const testState = {
            showOnModel: variantActive,
            inTest: variantActive || originalActive,
          };
          window.__stioOnModelVsFlatlayTestState = testState;
          // Keep legacy flag in sync for any other readers
          window.__stioOnModelVsFlatlayTestActive = testState.showOnModel;
          return testState;
        })
        .catch(() => inactiveState);
    }

    return window.__stioOnModelVsFlatlayTestPromise;
  };

  const initializeSwiper = () => {
    imageSwiper.value = new Swiper(imageCarouselRef.value, {
      slidesPerView: 1,
      wrapperClass: 'pc__slide-wrapper',
      slideClass: 'pc__slide',
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      a11y: {
        enabled: true,
      },
    });
  };
</script>
